import {NON_MATCHABLE_INSTRUCTION_STATUSES} from '@sollapay/enums';
import {pollingInterval} from '@sollapay/web-core';
import {useQuery} from '@tanstack/react-query';

import {useApiClient} from '@/hooks';

import {paymentsApi} from '../api';

import type {ApiResponse, PaymentInstructionDTO} from '@sollapay/types';

export const paymentKeys = {
  all: (trustAccountId: string) => ['trust-accounts', trustAccountId, 'payments'] as const,
  lists: (trustAccountId: string) => [...paymentKeys.all(trustAccountId), 'list'] as const
};

/** "Non-terminal" here means the same thing as `NON_MATCHABLE_INSTRUCTION_STATUSES` does for
 * bank-statement matching: the instruction's lifecycle isn't over yet, so it's still worth
 * polling for a status change. */
function hasNonTerminalPayment(cached: ApiResponse<PaymentInstructionDTO[]> | undefined): boolean {
  return cached?.data.some(p => !NON_MATCHABLE_INSTRUCTION_STATUSES.includes(p.status)) ?? false;
}

export function usePaymentsQuery(trustAccountId: string) {
  const client = useApiClient();

  return useQuery({
    queryKey: paymentKeys.lists(trustAccountId),
    queryFn: () => paymentsApi.fetchPayments(client, trustAccountId),
    enabled: !!trustAccountId,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: query =>
      hasNonTerminalPayment(query.state.data) ? pollingInterval.medium : false,
    select: response => response.data
  });
}
